import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { listEvents, createEvent, getEventByCode } from "@/lib/api";
import { listLocalTrips, rememberTrip } from "@/lib/access";
import { useAuth, startGoogleLogin } from "@/context/AuthContext";
import { initials } from "@/lib/scoring";
import { Trophy, Plus, Hash, ChevronRight, Users, CalendarDays, LogIn, LogOut, UserCircle2 } from "lucide-react";
import InstallBanner from "@/screens/InstallBanner";

export default function Home() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [creating, setCreating] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [filter, setFilter] = useState("all"); // "all" | "mine"
  const navigate = useNavigate();
  const { user, signOut } = useAuth();

  // Only trips you organise (server, when signed in) or have opened with a valid code on this device
  const load = async () => {
    try {
      setLoading(true);
      const local = listLocalTrips();
      let server = [];
      if (user) {
        try { server = await listEvents(); } catch (e) { console.error("listEvents failed", e); toast.error("Could not load your trips"); }
      }
      const byId = {};
      for (const ev of server) byId[ev.id] = ev;
      for (const [id, t] of Object.entries(local)) {
        if (!byId[id]) byId[id] = { id, code: t.code, name: t.name || "Trip", createdAt: t.openedAt, local: true };
      }
      let list = Object.values(byId).sort((a, b) => (b.createdAt || "").localeCompare(a.createdAt || ""));
      if (filter === "mine" && user) list = list.filter((ev) => ev.ownerUserId === user.user_id);
      setEvents(list);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter, user]);

  const onCreate = async () => {
    if (!name.trim()) { toast.error("Give the trip a name"); return; }
    setCreating(true);
    try {
      const ev = await createEvent(name.trim());
      rememberTrip({ id: ev.id, code: ev.code, name: ev.name });
      toast.success(`Trip "${ev.name}" created`);
      navigate(`/trip/${ev.id}`);
    } catch (e) {
      toast.error("Could not create trip");
    } finally {
      setCreating(false);
    }
  };

  const onJoin = async () => {
    const c = code.trim().toUpperCase();
    if (!c) return;
    try {
      const ev = await getEventByCode(c);
      rememberTrip({ id: ev.id, code: ev.code, name: ev.name });
      setCode("");
      toast.success(`Joined "${ev.name}" — it's now in your trips`);
      load();
    } catch {
      toast.error("Trip code not found");
    }
  };

  return (
    <div className="max-w-xl mx-auto px-5 pt-10 pb-16 tga-fade">
      {/* Auth chip */}
      <div className="flex items-center justify-end gap-2 mb-2">
        {user ? (
          <>
            <div className="flex items-center gap-2" data-testid="home-user-chip">
              {user.picture ? (
                <img src={user.picture} alt="" className="w-7 h-7 rounded-full border border-[color:var(--border-brass)]" />
              ) : (
                <div className="avatar avatar-dark" style={{ width: 28, height: 28, fontSize: 12 }}>{initials(user.name || user.email || "?")}</div>
              )}
              <span className="text-xs text-[color:var(--muted-green)] truncate max-w-[140px]">{user.name || user.email}</span>
            </div>
            <button data-testid="home-profile-btn" onClick={() => navigate("/profile")} className="ghost-btn rounded-lg p-2" title="My profile"><UserCircle2 size={14} /></button>
            <button data-testid="home-signout-btn" onClick={signOut} className="ghost-btn rounded-lg p-2" title="Sign out"><LogOut size={14} /></button>
          </>
        ) : (
          <button data-testid="home-signin-btn" onClick={startGoogleLogin} className="brass-btn rounded-lg px-3 py-2 text-sm inline-flex items-center gap-1">
            <LogIn size={14} /> Sign in with Google
          </button>
        )}
      </div>

      <div className="text-center mb-10">
        <img src="/logo-rounded.png" alt="TGA Golf" className="crest crest-lg crest-img mx-auto mb-5" data-testid="home-crest" />
        <h1 className="font-display text-4xl sm:text-5xl font-semibold text-[color:var(--brass-bright)]">The TGA</h1>
        <p className="mt-2 text-[color:var(--muted-green)] italic font-display text-lg">Trip golf, tracked properly.</p>
      </div>

      <InstallBanner />

      {/* Join by code */}
      <div className="tga-card-dark mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Hash size={16} className="text-[color:var(--brass)]" />
          <span className="font-display text-[color:var(--brass-bright)] text-lg">Join a trip</span>
        </div>
        <div className="flex gap-2">
          <input
            data-testid="home-code-input"
            className="tga-input w-full font-mono-metrics uppercase tracking-widest"
            placeholder="ENTER CODE"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
          />
          <button data-testid="home-join-btn" className="brass-btn px-5 rounded-lg" onClick={onJoin}>Join</button>
        </div>
        <p className="helptext mt-2">Trips are private — only the organiser and players who enter the 6-character code can see them.</p>
      </div>

      {/* Trips list */}
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <h2 className="section-title mb-0 border-none">Your trips</h2>
        <div className="flex items-center gap-2">
          {user && (
            <div className="pill-toggle" style={{ borderColor: "var(--border-brass)" }}>
              <button data-testid="home-filter-all" className={filter === "all" ? "on" : ""} onClick={() => setFilter("all")} style={{ color: filter === "all" ? "#14251b" : "var(--text-cream)" }}>All</button>
              <button data-testid="home-filter-mine" className={filter === "mine" ? "on" : ""} onClick={() => setFilter("mine")} style={{ color: filter === "mine" ? "#14251b" : "var(--text-cream)" }}>Mine</button>
            </div>
          )}
          <button
            data-testid="home-new-trip-toggle"
            className="brass-btn px-4 py-2 rounded-lg text-sm inline-flex items-center gap-1"
            onClick={() => setShowNew((s) => !s)}
          >
            <Plus size={16} /> New trip
          </button>
        </div>
      </div>

      {showNew && (
        <div className="tga-card mb-4 tga-fade">
          <label className="block text-sm font-medium mb-1">Trip name</label>
          <input
            data-testid="home-newtrip-name"
            className="tga-input-light w-full"
            placeholder="e.g. Yarrawonga 2026"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <div className="flex gap-2 mt-3">
            <button data-testid="home-create-trip-btn" disabled={creating} className="brass-btn px-4 py-2 rounded-lg" onClick={onCreate}>
              {creating ? "Creating…" : "Create trip"}
            </button>
            <button className="ghost-btn px-4 py-2 rounded-lg text-sm" onClick={() => setShowNew(false)}>Cancel</button>
          </div>
        </div>
      )}

      {loading ? (
        <p className="helptext text-center py-8">Loading…</p>
      ) : events.length === 0 ? (
        <div className="tga-card-dark text-center py-10">
          <div className="font-display text-2xl text-[color:var(--brass-bright)] mb-2">No trips yet</div>
          <p className="helptext">Set up your first tournament to start tracking scores, Stableford points and the side games.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {events.map((ev) => (
            <button
              key={ev.id}
              data-testid={`home-trip-card-${ev.code}`}
              onClick={() => navigate(`/trip/${ev.id}`)}
              className="w-full text-left tga-card flex items-center gap-3 hover:shadow-lg"
            >
              <img src="/logo-rounded.png" alt="TGA Golf" className="crest crest-img" />
              <div className="flex-1 min-w-0">
                <div className="font-display text-xl truncate">{ev.name}</div>
                <div className="text-xs text-[color:var(--brass-dark)] mt-0.5 flex items-center gap-3">
                  <span className="font-mono-metrics tracking-widest">{ev.code}</span>
                  {ev.local ? (
                    <span className="italic">joined on this phone</span>
                  ) : (
                    <>
                      <span className="inline-flex items-center gap-1"><Users size={12} /> {ev.playerCount}</span>
                      <span className="inline-flex items-center gap-1"><CalendarDays size={12} /> {ev.dayCount}</span>
                    </>
                  )}
                </div>
              </div>
              <ChevronRight size={18} className="text-[color:var(--brass-dark)]" />
            </button>
          ))}
        </div>
      )}

      <p className="helptext text-center mt-8 flex items-center justify-center gap-3">
        <span className="inline-flex items-center gap-1"><Trophy size={12} /> Scores sync live across every phone.</span>
        {user && (
          <button data-testid="home-season-link" onClick={() => navigate("/season")} className="underline hover:text-[color:var(--brass)]">Season standings →</button>
        )}
      </p>

      <p className="helptext text-center mt-4 flex items-center justify-center gap-3">
        <button
          data-testid="home-privacy-link"
          onClick={() => navigate("/privacy")}
          className="hover:text-[color:var(--brass)] hover:underline"
        >
          Privacy
        </button>
        <span className="text-[color:var(--brass-dark)]">·</span>
        <button
          data-testid="home-terms-link"
          onClick={() => navigate("/terms")}
          className="hover:text-[color:var(--brass)] hover:underline"
        >
          Terms
        </button>
      </p>
    </div>
  );
}
